import React, { useState, useRef } from 'react';
import { 
  X, Plus, Trash2, FileText, Download, Send, 
  Building2, Mail, Phone, MapPin, Calendar,
  Printer, Copy, Check, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Invoice Preview Component - Matches the NEURA AI SOFTWARES PDF style
const InvoicePreview = React.forwardRef(({ invoice, onClose }, ref) => {
  const formatDate = (dateStr) => {
    if (!dateStr) {
      const now = new Date();
      return now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `£${Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm overflow-auto py-8" onClick={onClose}>
      <div 
        ref={ref}
        className="max-w-[850px] mx-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="invoice-preview"
      >
        {/* PAGE 1 - Header and Cost Breakdown */}
        <div className="min-h-[1100px] relative">
          {/* Navy Header */}
          <div className="bg-[#0f172a] text-white px-10 py-8">
            <div className="flex items-start justify-between">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">N</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">NEURA AI</h1>
                  <p className="text-cyan-400 text-sm font-medium">AGENCY</p>
                </div>
              </div>
              
              {/* Invoice Title */}
              <div className="text-right">
                <h2 className="text-3xl font-light text-white/90">PROJECT PROPOSAL</h2>
                <p className="text-cyan-400 text-sm mt-1">& Cost Breakdown</p>
              </div>
            </div>

            {/* Invoice Details Row */}
            <div className="flex justify-between items-end mt-8 pt-6 border-t border-white/20">
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Prepared For</p>
                <p className="text-xl font-semibold">{invoice.client_company || invoice.client_name}</p>
                {invoice.client_name && invoice.client_company && (
                  <p className="text-gray-400 text-sm">{invoice.client_name}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Invoice</p>
                <p className="text-xl font-mono">{invoice.invoice_number}</p>
                <p className="text-gray-400 text-sm">{formatDate(invoice.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Project Title Section */}
          <div className="px-10 py-6 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border-b border-gray-200">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Project</p>
            <h3 className="text-xl font-semibold text-gray-900">{invoice.project_title}</h3>
          </div>

          {/* Cost Breakdown Section */}
          <div className="px-10 py-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-cyan-500 font-mono text-sm">01 /</span>
              <h4 className="text-lg font-semibold text-gray-900">Detailed Cost Breakdown</h4>
            </div>

            {/* Line Items Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 bg-[#0f172a] text-white text-xs font-medium">
                <div className="col-span-1 px-4 py-3 text-center">#</div>
                <div className="col-span-3 px-4 py-3">SERVICE / DELIVERABLE</div>
                <div className="col-span-5 px-4 py-3">DETAIL</div>
                <div className="col-span-3 px-4 py-3 text-right">TOTAL (GBP)</div>
              </div>

              {/* Table Body */}
              {invoice.line_items?.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`grid grid-cols-12 text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-t border-gray-100`}
                >
                  <div className="col-span-1 px-4 py-3 text-center text-gray-400 font-mono">{idx + 1}</div>
                  <div className="col-span-3 px-4 py-3 font-medium text-gray-900">{item.service}</div>
                  <div className="col-span-5 px-4 py-3 text-gray-600">{item.description}</div>
                  <div className="col-span-3 px-4 py-3 text-right font-semibold text-gray-900">
                    {formatCurrency(item.amount)}
                  </div>
                </div>
              ))}
            </div>

            {/* Totals Section */}
            <div className="mt-6 flex justify-end">
              <div className="w-80">
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
                </div>
                {invoice.discount > 0 && (
                  <div className="flex justify-between py-2 text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(invoice.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 text-sm border-t border-gray-200">
                  <span className="text-gray-600">Net Total</span>
                  <span className="font-medium">{formatCurrency(invoice.net_total)}</span>
                </div>
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-gray-600">VAT ({invoice.vat_rate}%)</span>
                  <span className="font-medium">{formatCurrency(invoice.vat_amount)}</span>
                </div>
                <div className="flex justify-between py-3 text-lg font-bold bg-[#0f172a] text-white px-4 rounded-lg mt-2">
                  <span>Total Due</span>
                  <span>{formatCurrency(invoice.total_due)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PAGE 2 - Payment Schedule & Terms */}
        <div className="min-h-[600px] border-t-4 border-cyan-500">
          {/* Payment Schedule */}
          <div className="px-10 py-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-cyan-500 font-mono text-sm">02 /</span>
              <h4 className="text-lg font-semibold text-gray-900">Payment Schedule</h4>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-5 border border-cyan-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-cyan-700 bg-cyan-100 px-2 py-1 rounded">Phase 1</span>
                  <span className="text-2xl font-bold text-cyan-700">33%</span>
                </div>
                <p className="text-sm text-gray-600">Deposit on project commencement</p>
                <p className="text-lg font-semibold text-gray-900 mt-2">{formatCurrency(invoice.total_due * 0.33)}</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border border-purple-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-1 rounded">Phase 2</span>
                  <span className="text-2xl font-bold text-purple-700">33%</span>
                </div>
                <p className="text-sm text-gray-600">Mid-project milestone delivery</p>
                <p className="text-lg font-semibold text-gray-900 mt-2">{formatCurrency(invoice.total_due * 0.33)}</p>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border border-emerald-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded">Phase 3</span>
                  <span className="text-2xl font-bold text-emerald-700">34%</span>
                </div>
                <p className="text-sm text-gray-600">Final delivery & sign-off</p>
                <p className="text-lg font-semibold text-gray-900 mt-2">{formatCurrency(invoice.total_due * 0.34)}</p>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="px-10 py-6 bg-gray-50 border-t border-gray-200">
            <h5 className="text-sm font-semibold text-gray-900 mb-4">Bank Details for Payment</h5>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs mb-1">Account Name</p>
                <p className="font-medium">Peter Gwaze</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">Sort Code</p>
                <p className="font-mono font-medium">04-29-09</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">Account Number</p>
                <p className="font-mono font-medium">99650703</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">Bank</p>
                <p className="font-medium">Revolut Ltd</p>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="px-10 py-6">
            <h5 className="text-sm font-semibold text-gray-900 mb-3">Terms & Conditions</h5>
            <div className="text-xs text-gray-600 space-y-1">
              <p>• {invoice.payment_terms}</p>
              <p>• Copyright and ownership of all deliverables remains with NEURA AI SOFTWARES until full payment is received.</p>
              <p>• This quote is valid for 30 days from the date of issue.</p>
              <p>• Any additional work requested beyond the agreed scope will be quoted separately.</p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-10 py-6 bg-[#0f172a] text-white mt-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm">
                <a href="https://neura-ai.co.uk" className="text-cyan-400 hover:underline">neura-ai.co.uk</a>
                <span className="text-gray-400">hello@neura-ai.co.uk</span>
              </div>
              <div className="text-right text-sm text-gray-400">
                <p>Ref: {invoice.invoice_number}</p>
                <p>{formatDate(invoice.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="fixed top-4 right-4 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-6 h-6 text-gray-600" />
        </button>
      </div>
    </div>
  );
});

// Main Invoice Generator Component
const InvoiceGenerator = ({ token, onClose, editInvoice = null, onSave }) => {
  const [formData, setFormData] = useState(editInvoice ? {
    client_name: editInvoice.client_name || '',
    client_email: editInvoice.client_email || '',
    client_company: editInvoice.client_company || '',
    client_address: editInvoice.client_address || '',
    project_title: editInvoice.project_title || '',
    discount: editInvoice.discount || 0,
    vat_rate: editInvoice.vat_rate || 20,
    notes: editInvoice.notes || '',
    payment_terms: editInvoice.payment_terms || 'Payment due within 14 days. Late payments incur 2% interest after 14 days.',
    due_date: editInvoice.due_date || ''
  } : {
    client_name: '',
    client_email: '',
    client_company: '',
    client_address: '',
    project_title: '',
    discount: 0,
    vat_rate: 20,
    notes: '',
    payment_terms: 'Payment due within 14 days. Late payments incur 2% interest after 14 days.',
    due_date: ''
  });

  const [lineItems, setLineItems] = useState(editInvoice?.line_items || [
    { service: '', description: '', amount: 0 }
  ]);

  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const invoiceRef = useRef(null);

  const addLineItem = () => {
    setLineItems([...lineItems, { service: '', description: '', amount: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setLineItems(updated);
  };

  // Calculate totals
  const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netTotal = subtotal - (formData.discount || 0);
  const vatAmount = netTotal * (formData.vat_rate / 100);
  const totalDue = netTotal + vatAmount;

  const previewInvoice = {
    ...formData,
    line_items: lineItems,
    subtotal,
    net_total: netTotal,
    vat_amount: vatAmount,
    total_due: totalDue,
    invoice_number: editInvoice?.invoice_number || 'NRA-2026-DRAFT',
    created_at: editInvoice?.created_at || new Date().toISOString()
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    
    // Validation
    if (!formData.client_name.trim()) {
      setError('Client name is required');
      return;
    }
    if (!formData.project_title.trim()) {
      setError('Project title is required');
      return;
    }
    if (lineItems.length === 0 || !lineItems[0].service) {
      setError('At least one line item is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        line_items: lineItems.filter(item => item.service.trim())
      };

      const url = editInvoice 
        ? `${BACKEND_URL}/api/invoices/${editInvoice.invoice_id}`
        : `${BACKEND_URL}/api/invoices/create`;
      
      const method = editInvoice ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(editInvoice ? 'Invoice updated!' : 'Invoice created!');
        if (onSave) onSave(data);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save invoice');
      }
    } catch (err) {
      setError('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    setShowPreview(true);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  return (
    <>
      <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm overflow-auto py-4" onClick={onClose}>
        <div 
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="invoice-generator"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg">
                  {editInvoice ? 'Edit Invoice' : 'Create New Invoice'}
                </h2>
                <p className="text-gray-400 text-sm">NEURA AI SOFTWARES</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                <Check className="w-4 h-4" /> {success}
              </div>
            )}

            {/* Client Details */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" /> Client Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-600">Client Name *</label>
                  <Input 
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Contact name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Company Name</label>
                  <Input 
                    value={formData.client_company}
                    onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                    placeholder="Company name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Email</label>
                  <Input 
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="client@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Address</label>
                  <Input 
                    value={formData.client_address}
                    onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                    placeholder="Client address"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" /> Project Details
              </h3>
              <div>
                <label className="text-xs text-gray-600">Project Title *</label>
                <Input 
                  value={formData.project_title}
                  onChange={(e) => setFormData({...formData, project_title: e.target.value})}
                  placeholder="e.g., Website Development & Tracking System"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Line Items</h3>
                <Button size="sm" variant="outline" onClick={addLineItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="space-y-3">
                {lineItems.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full text-xs font-mono">
                      {idx + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-500">Service/Deliverable</label>
                        <Input 
                          value={item.service}
                          onChange={(e) => updateLineItem(idx, 'service', e.target.value)}
                          placeholder="e.g., UI/UX Design"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Description</label>
                        <Input 
                          value={item.description}
                          onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                          placeholder="e.g., Wireframes, prototypes"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Amount (£)</label>
                        <Input 
                          type="number"
                          value={item.amount || ''}
                          onChange={(e) => updateLineItem(idx, 'amount', e.target.value)}
                          placeholder="0"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <button 
                      onClick={() => removeLineItem(idx)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      disabled={lineItems.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Options */}
            <div className="mb-6 grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-600">Discount (£)</label>
                <Input 
                  type="number"
                  value={formData.discount || ''}
                  onChange={(e) => setFormData({...formData, discount: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">VAT Rate (%)</label>
                <Input 
                  type="number"
                  value={formData.vat_rate}
                  onChange={(e) => setFormData({...formData, vat_rate: parseFloat(e.target.value) || 0})}
                  placeholder="20"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Due Date</label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Totals Summary */}
            <div className="mb-6 p-4 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl text-white">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Subtotal</p>
                  <p className="text-lg font-semibold">£{subtotal.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Discount</p>
                  <p className="text-lg font-semibold text-green-400">-£{(formData.discount || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">VAT ({formData.vat_rate}%)</p>
                  <p className="text-lg font-semibold">£{vatAmount.toLocaleString()}</p>
                </div>
                <div className="bg-gradient-to-r from-cyan-500 to-purple-500 rounded-lg p-2">
                  <p className="text-white/80 text-xs mb-1">Total Due</p>
                  <p className="text-xl font-bold">£{totalDue.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="mb-6">
              <label className="text-xs text-gray-600">Payment Terms</label>
              <textarea 
                value={formData.payment_terms}
                onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                rows={2}
                placeholder="Payment terms..."
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              <FileText className="w-4 h-4 mr-2" /> Preview
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              >
                {saving ? 'Saving...' : (editInvoice ? 'Update Invoice' : 'Create Invoice')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Preview Modal */}
      {showPreview && (
        <InvoicePreview 
          ref={invoiceRef}
          invoice={previewInvoice} 
          onClose={() => setShowPreview(false)} 
        />
      )}
    </>
  );
};

export default InvoiceGenerator;
export { InvoicePreview };
